import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import HeroIntro from '../components/HeroIntro';
import ReligiousTicker from '../components/ReligiousTicker';
import Navbar from '../components/Navbar';
import HomepageStatsSection from '../components/HomepageStatsSection';
import FeaturedProjectsSection from '../components/FeaturedProjectsSection';
import { getPublicActualites } from '../services/actualiteService';
import { getPublicProgrammes } from '../services/siteContentService';
import {
  FaHeart,
  FaHandsHelping,
  FaUsers,
  FaSchool,
  FaCalendarAlt,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { dateLocaleOf, usePublicLocale, usePublicPaths } from '../routes/publicPaths';
import VolunteerRequestTrigger from '../components/VolunteerRequestTrigger';
import { PROGRAM_ICON_CLASSES } from '../config/brandAssets';
import { normalizeStorageUrl } from '../config/apiBase';
import { tProgramDescription, tProgramTitle } from '../utils/publicI18n';

const PROGRAM_ICON_COMPONENTS = [FaHeart, FaHandsHelping, FaSchool, FaUsers];

const renderProgramIcon = (index) => {
  const Icon = PROGRAM_ICON_COMPONENTS[index] || FaHeart;
  return <Icon className={PROGRAM_ICON_CLASSES[index] || PROGRAM_ICON_CLASSES[0]} />;
};

const DEFAULT_PROGRAMS = [
  {
    id: 1,
    slug: 'adahi',
    title: 'أضحية العيد',
    description: 'الرعاية الفورية للحالات الطبية الطارئة، مخففةً أعباء المرضى وذويهم.',
    iconIndex: 0,
    stats: '',
  },
  {
    id: 2,
    slug: 'namaa',
    title: 'نـماء',
    description: 'تنفيذ مشاريع دخل لتحسين مستوى المعيشة للأسر الفقيرة.',
    iconIndex: 1,
    stats: '',
  },
  {
    id: 3,
    slug: 'kafala',
    title: 'كفالة',
    description:
      '"أيتامنا" هو مشروع إنساني يهدف إلى توفير رعاية شاملة للأيتام، من خلال تقديم الدعم الاجتماعي والتربوي والتعليمي والصحي، بما يسهم في تحسين جودة حياتهم، وتنمية قدراتهم، وتمكينهم من بناء مستقبل أكثر استقرارًا وإشراقًا.',
    iconIndex: 2,
    stats: '',
  },
  {
    id: 4,
    slug: 'saqya',
    title: 'سقيا',
    description:
      'يأتي مشروع "سقيا عطاء" استجابةً لحاجة الساكنة إلى المياه النقية، حيث يعمل على توفير مصادر مستدامة لمياه الشرب من خلال حفر وتجهيز الآبار وتسهيل وصول المياه إلى الأسر بالمناطق والدواوير التي تعاني من ندرتها.',
    iconIndex: 3,
    stats: '',
  },
];

const HomePage = () => {
  const { t } = useTranslation();
  const locale = usePublicLocale();
  const paths = usePublicPaths();
  const dateLocale = dateLocaleOf(locale);
  // Charger les actualités pour la section actualités
  const [actualites, setActualites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActualites = async () => {
      try {
        const data = await getPublicActualites({ limit: 3 });
        setActualites(data);
      } catch (error) {
        console.error('Erreur lors du chargement des actualités:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActualites();
  }, []);

  // Programmes affichés sur l'accueil
  const [programs, setPrograms] = useState(() => {
    try {
      const raw = localStorage.getItem('khayr_home_programs_v3');
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length >= 4 ? parsed : DEFAULT_PROGRAMS;
    } catch {
      return DEFAULT_PROGRAMS;
    }
  });

  useEffect(() => {
    getPublicProgrammes()
      .then((data) => {
        if (data?.length >= 4) {
          const mapped = data.slice(0, 4).map((p, i) => ({
            id: p.id,
            slug: p.slug === 'ihyaa' ? 'adahi' : p.slug === 'imaar' ? 'ramadan' : p.slug,
            title: p.title,
            description: p.description,
            iconIndex: i,
            stats: '',
          }));
          setPrograms(mapped);
          try {
            localStorage.setItem('khayr_home_programs_v3', JSON.stringify(mapped));
          } catch {
            // ignore quota errors
          }
        }
      })
      .catch(() => {});
  }, []);

  // Témoignages
  const testimonials = [1, 2, 3].map((id) => ({
    id,
    name: t(`public.testimonials.${id}.name`),
    role: t(`public.testimonials.${id}.role`),
    content: t(`public.testimonials.${id}.content`),
  }));

  return (
    <div className="flex flex-col overflow-x-hidden">
      <Helmet>
        <title>{t('public.home.metaTitle')}</title>
        <meta
          name="description"
          content={t('public.home.metaDescription')}
        />
        <meta name="keywords" content="جمعية خيرية, تبرع, عمل إنساني, مساعدة, المغرب" />
        <meta property="og:title" content={t('public.home.metaTitle')} />
        <meta
          property="og:description"
          content={t('public.home.metaDescription')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sohbatkheir.ma" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Premier écran = header + slider + barre bleue (Aya visible au scroll) */}
      <div className="home-above-fold min-h-dvh flex flex-col">
        <Navbar />
        <div id="accueil" className="hero-first-screen">
          <HeroSlider />
          <ReligiousTicker />
        </div>
      </div>

      <section className="hero-block">
        <HeroIntro />
      </section>

      <HomepageStatsSection />
      <FeaturedProjectsSection />

      {/* Programmes Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('public.home.programsTitle')}</h2>
            <div className="w-24 h-1 bg-brand-divider mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {programs.map((program) => (
              <Link
                key={program.id}
                to={program.slug ? paths.programme(program.slug) : paths.programmes}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-3xl">
                    {renderProgramIcon(program.iconIndex)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{tProgramTitle(t, program.slug, program.title)}</h3>
                  <p className="text-gray-600 mb-4">{tProgramDescription(t, program.slug, program.description)}</p>
                  {program.stats && <p className="text-brand-primary font-medium">{program.stats}</p>}
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to={paths.programmes} 
              className="inline-block bg-brand-primary hover:bg-brand-primary-dark text-white font-medium py-3 px-8 rounded-md transition-colors"
            >
              {t('public.home.allPrograms')}
            </Link>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('public.home.thanksTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('public.home.thanksLead')}</p>
            <div className="w-24 h-1 bg-brand-divider mx-auto mt-4"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-brand-surface flex items-center justify-center text-brand-primary text-xl font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="mr-3">
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-cta text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('public.home.ctaTitle')}</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-white/80">{t('public.home.ctaLead')}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to={paths.donate} 
              className="bg-brand-red hover:bg-brand-red-dark text-white font-medium py-3 px-8 rounded-md transition-colors"
            >
              {t('public.home.donate')}
            </Link>
            <VolunteerRequestTrigger
              className="bg-brand-blue hover:bg-brand-blue-dark border-2 border-brand-blue text-white font-medium py-3 px-8 rounded-md transition-colors"
            >
              {t('public.home.volunteer')}
            </VolunteerRequestTrigger>
          </div>
        </div>
      </section>

      {/* Dernières actualités */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{t('public.home.latestNews')}</h2>
              <div className="w-24 h-1 bg-brand-divider mt-2"></div>
            </div>
            <Link to={paths.actualites} className="text-brand-primary hover:text-brand-primary-dark font-medium">
              {t('public.home.viewAll')}
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
            </div>
          ) : actualites.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {actualites.slice(0, 3).map((actualite) => (
                <Link
                  key={actualite.id}
                  to={paths.actualite(actualite.id)}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="h-48 bg-gray-200 relative">
                    {actualite.image_url || actualite.image ? (
                      <img
                        src={normalizeStorageUrl(actualite.image_url || actualite.image)}
                        alt={actualite.titre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <span className="text-gray-400">{t('public.home.noImage')}</span>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                      <div className="flex items-center text-white text-sm">
                        <FaCalendarAlt className="ml-1" />
                        <span>{new Date(actualite.date).toLocaleDateString(dateLocale)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{actualite.titre}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{actualite.description}</p>
                    <span className="text-brand-primary font-medium inline-flex items-center">
                      {t('public.home.readMore')}
                      <svg className="mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('public.home.noNews')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
